import { router } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'

import { titleize } from '../../utils/string'

function FlashAlert({ variant, message, onDismiss, textDark }) {
  if (!message) return null

  const className = [
    'alert',
    `alert-${variant}`,
    textDark ? 'text-dark' : '',
    'alert-dismissible',
    'fade',
    'show',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} role="alert">
      <h4 className="alert-heading mb-0">{message}</h4>
      <button
        type="button"
        className="btn-close"
        data-bs-dismiss="alert"
        aria-label="Close"
        onClick={onDismiss}
      ></button>
    </div>
  )
}

function ErrorText({ errors, name }) {
  const msg = errors?.[name]
  if (!msg) return null
  const text = Array.isArray(msg) ? msg.join(', ') : String(msg)
  return <div className="invalid-feedback d-block">{text}</div>
}

function formatBalance(value) {
  if (value == null) return '0'
  return Number(value).toFixed(2).replace(/\.00$/, '')
}

function toDate(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function daysBetween(start, end) {
  const ms = end.getTime() - start.getTime()
  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1
}

export default function Index({ leave_history, leave_requests, balances, limits, errors, form, notice, alert }) {
  const [flashNotice, setFlashNotice] = useState(notice)
  const [flashAlert, setFlashAlert] = useState(alert)
  const [state, setState] = useState({
    leave_type: form?.leave_type || 'vacation',
    day_unit: form?.day_unit || 'whole',
    start_date: form?.start_date || '',
    end_date: form?.end_date || '',
    reason: form?.reason || '',
    turnover_plan: form?.turnover_plan || '',
  })

  useEffect(() => {
    if (notice) setFlashNotice(notice)
    if (alert) setFlashAlert(alert)
  }, [notice, alert])

  const dateRange = useMemo(() => {
    const start = toDate(state.start_date)
    const end = toDate(state.end_date)
    if (!start || !end) return null
    if (end < start) return null
    return daysBetween(start, end)
  }, [state.start_date, state.end_date])

  const totalDays = useMemo(() => {
    if (!dateRange) return 0
    return state.day_unit === 'half' ? dateRange * 0.5 : dateRange
  }, [dateRange, state.day_unit])

  const projectedBalance = useMemo(() => {
    const current = balances?.[state.leave_type] ?? 0
    return current - totalDays
  }, [balances, state.leave_type, totalDays])

  const hasCompleteDateRange = Boolean(state.start_date && state.end_date && dateRange)

  const negativeWarning = totalDays > 0 && projectedBalance < 0

  const submit = (e) => {
    e.preventDefault()
    router.post('/leaves', {
      leave_request: {
        leave_type: state.leave_type,
        day_unit: state.day_unit,
        start_date: state.start_date,
        end_date: state.end_date,
        reason: state.reason,
        turnover_plan: state.turnover_plan,
      },
    })
  }

  const history = leave_history || leave_requests || []

  return (
    <div className="container-fluid">
      <div className="header">
        <div className="header-body">
          <div className="row align-items-center">
            <div className="col">
              <h6 className="header-pretitle">Personal</h6>
              <h1 className="header-title text-truncate">Leaves</h1>
            </div>
          </div>
        </div>
      </div>

      <FlashAlert variant="danger" message={flashAlert} onDismiss={() => setFlashAlert(null)} />
      <FlashAlert variant="success" message={flashNotice} textDark onDismiss={() => setFlashNotice(null)} />

      <div className="row">
        <div className="col-12 col-xl-4">
          <div className="card">
            <div className="card-header">
              <h4 className="card-header-title">Current Leave Balance</h4>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <span>Sick Leave</span>
                <strong>{formatBalance(balances?.sick)}</strong>
              </div>
              <div className="d-flex justify-content-between mt-2">
                <span>Vacation Leave</span>
                <strong>{formatBalance(balances?.vacation)}</strong>
              </div>
              {hasCompleteDateRange ? (
                <div className="mt-3 small text-body-secondary">
                  Requested: {totalDays} day(s) · {titleize(state.leave_type)} balance after selected dates:{' '}
                  {formatBalance(projectedBalance)}
                </div>
              ) : null}
              {negativeWarning ? (
                <div className="alert alert-warning mt-3 mb-0">
                  This request will make your balance go negative. You can still submit it.
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-8">
          <div className="card">
            <div className="card-header">
              <h4 className="card-header-title">File a Leave Request</h4>
            </div>
            <div className="card-body">
              <form onSubmit={submit}>
                <div className="row">
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">Leave Type</label>
                      <select
                        className="form-select"
                        value={state.leave_type}
                        onChange={(e) => setState((s) => ({ ...s, leave_type: e.target.value }))}
                      >
                        <option value="sick">Sick Leave</option>
                        <option value="vacation">Vacation Leave</option>
                      </select>
                      <ErrorText errors={errors} name="leave_type" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">Day Unit</label>
                      <select
                        className="form-select"
                        value={state.day_unit}
                        onChange={(e) => setState((s) => ({ ...s, day_unit: e.target.value }))}
                      >
                        <option value="whole">Whole Day</option>
                        <option value="half">Half Day</option>
                      </select>
                      <ErrorText errors={errors} name="day_unit" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        min={limits?.min_date}
                        value={state.start_date}
                        onChange={(e) => setState((s) => ({ ...s, start_date: e.target.value }))}
                      />
                      <ErrorText errors={errors} name="start_date" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        min={limits?.min_date}
                        value={state.end_date}
                        onChange={(e) => setState((s) => ({ ...s, end_date: e.target.value }))}
                      />
                      <ErrorText errors={errors} name="end_date" />
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-group">
                      <label className="form-label">Reason</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={state.reason}
                        onChange={(e) => setState((s) => ({ ...s, reason: e.target.value }))}
                      ></textarea>
                      <ErrorText errors={errors} name="reason" />
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-group">
                      <label className="form-label">Turnover Plan</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={state.turnover_plan}
                        onChange={(e) => setState((s) => ({ ...s, turnover_plan: e.target.value }))}
                      ></textarea>
                      <small className="text-body-secondary">
                        List important information (critical dates, etc.) and who will take over for your
                        responsibilities while you are away.
                      </small>
                      <ErrorText errors={errors} name="turnover_plan" />
                    </div>
                  </div>

                  <div className="col-12">
                    <button type="submit" className="btn btn-primary">
                      Submit Leave Request
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header">
          <h4 className="card-header-title">Leave History</h4>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-hover table-nowrap card-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Dates</th>
                <th>Days</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {history.map((request) => (
                <tr key={`${request.kind}-${request.id}`}>
                  <td>{titleize(request.leave_type)}</td>
                  <td>
                    {request.start_date_label}
                    {request.start_date !== request.end_date ? ` - ${request.end_date_label}` : ''}
                  </td>
                  <td>{request.total_days}</td>
                  <td>
                    <span
                      className={`badge text-bg-${
                        request.status === 'approved' ? 'success' : request.status === 'refilled' ? 'info' : 'warning'
                      }`}
                    >
                      {titleize(request.status)}
                    </span>
                  </td>
                  <td>{request.created_at_label}</td>
                </tr>
              ))}

              {!history.length ? (
                <tr>
                  <td colSpan={5} className="text-center text-body-secondary py-4">
                    No leave history yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
